<!-- src/components/rota/GlobalGMrotaTable.vue -->
<template>
  <div v-if="canView" class="section global-gm-rota-root" style="padding: 0">
    <RotaToolbar
      :week-range-text="weekRangeText"
      :can-go-next="true"
      :can-edit="false"
      :busy="loading"
      @prev="prevWeek"
      @next="nextWeek"
      @refresh="loadRows"
    />

    <div v-if="error" class="card-panel red lighten-5 red-text text-darken-2">
      {{ error }}
    </div>

    <div class="card">
      <div class="card-content">
        <div
          style="
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 12px;
            flex-wrap: wrap;
            margin-bottom: 10px;
          "
        >
          <div>
            <div style="font-size: 20px; font-weight: 700">General Manager Rota</div>

            <div class="grey-text text-darken-1" style="margin-top: 4px">
              All venues · operations only
            </div>
          </div>

          <div class="grey-text text-darken-1" style="font-size: 14px">
            {{ uniqueManagerCount }} General Manager<span v-if="uniqueManagerCount !== 1"
              >s</span
            >
            ·
            {{ totalShiftCount }} shift<span v-if="totalShiftCount !== 1">s</span>
            ·
            {{ formatHoursDisplay(totalHours) }}
          </div>
        </div>

        <p v-if="loading" class="grey-text" style="margin: 0">
          Loading General Manager rota…
        </p>

        <p
          v-else-if="!managerRows.length"
          class="grey-text text-darken-1"
          style="margin: 0"
        >
          No General Manager shifts found for this week.
        </p>

        <div v-else class="table-wrap">
          <table class="highlight bordered gm-rota-table">
            <thead>
              <tr>
                <th class="staff-col">General Manager</th>

                <th v-for="day in weekDays" :key="day.iso" class="day-col">
                  <div class="day-head">
                    <div class="day-name">
                      {{ formatDayHeader(day.iso) }}
                    </div>

                    <div class="day-date">
                      {{ formatDateDisplay(day.iso) }}
                    </div>
                  </div>
                </th>

                <th class="total-col right-align">Hours</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="manager in managerRows" :key="manager.key">
                <td class="staff-col">
                  <div style="font-weight: 700">
                    {{ manager.employeeName }}
                  </div>

                  <div
                    class="grey-text text-darken-1"
                    style="font-size: 12px; margin-top: 2px"
                  >
                    {{ manager.primaryVenueLabel || "Unknown venue" }}
                  </div>
                </td>

                <td
                  v-for="day in weekDays"
                  :key="`${manager.key}-${day.iso}`"
                  class="day-col"
                >
                  <div v-if="manager.days[day.iso]?.length" class="shift-stack">
                    <div
                      v-for="shift in manager.days[day.iso]"
                      :key="shift.key"
                      class="shift-chip-wrap"
                    >
                      <div class="shift-chip" :style="shiftChipStyle(shift)">
                        <div class="shift-chip-time">
                          {{ formatShiftDisplay(shift.startTime, shift.endTime) }}
                        </div>

                        <div class="shift-chip-meta">
                          {{ shift.venueLabel }}
                        </div>

                        <div
                          v-if="shift.breakMinutes > 0 || shift.paidHours > 0"
                          class="shift-chip-meta"
                        >
                          <span v-if="shift.breakMinutes > 0">
                            Break {{ formatBreakDisplay(shift.breakMinutes) }}
                          </span>

                          <span v-if="shift.breakMinutes > 0 && shift.paidHours > 0">
                            ·
                          </span>

                          <span v-if="shift.paidHours > 0">
                            {{ formatHoursDisplay(shift.paidHours) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-else class="empty-cell">—</div>
                </td>

                <td class="total-col right-align" style="font-weight: 700">
                  {{ formatHoursDisplay(manager.totalHours) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";

import { auth, db } from "@/firebase";

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import RotaToolbar from "@/components/rota/RotaToolbar.vue";

import * as formatter from "@/scripts/sharedHelpers/formatter";

import * as auditColours from "@/scripts/sharedHelpers/auditColours";

/* =========================================================
   Access
========================================================= */

const canView = ref(false);

/* =========================================================
   State
========================================================= */

const loading = ref(false);

const error = ref("");

const selectedWeekEnding = ref(getCurrentWeekEndingISO());

const gmStaffRows = ref([]);

const gmShiftRows = ref([]);

const venueNameMap = ref({});

/* =========================================================
   Lifecycle
========================================================= */

onMounted(async () => {
  await resolveAccess();

  if (canView.value) {
    await loadRows();
  }
});

/* =========================================================
   Computed
========================================================= */

const weekBounds = computed(() => getWeekBoundsFromWeekEnding(selectedWeekEnding.value));

const weekDays = computed(() => {
  const out = [];

  const start = parseISODateLocal(weekBounds.value.weekStart);

  for (let i = 0; i < 7; i += 1) {
    const d = new Date(start);

    d.setDate(start.getDate() + i);

    out.push({
      iso: toISODateLocal(d),
    });
  }

  return out;
});

const weekRangeText = computed(() => {
  return `${formatDateDisplay(weekBounds.value.weekStart)} - ${formatDateDisplay(
    weekBounds.value.weekEnd
  )}`;
});

const uniqueManagerCount = computed(() => managerRows.value.length);

const totalShiftCount = computed(() => gmShiftRows.value.length);

const totalHours = computed(() =>
  gmShiftRows.value.reduce((sum, row) => sum + toNumber(row.paidHours), 0)
);

const managerRows = computed(() => {
  const map = new Map();

  for (const gm of gmStaffRows.value) {
    const key = gm.key;

    map.set(key, {
      key,

      employeeName: gm.employeeName,

      primaryVenueLabel: gm.venueLabel || "",

      venueId: gm.venueId || "",

      venues: new Set(gm.venueLabel ? [gm.venueLabel] : []),

      days: Object.fromEntries(weekDays.value.map((d) => [d.iso, []])),

      totalHours: 0,
    });
  }

  for (const shift of gmShiftRows.value) {
    const managerKey = shift.managerKey;

    if (!map.has(managerKey)) {
      continue;
    }

    const row = map.get(managerKey);

    if (shift.venueLabel) {
      row.venues.add(shift.venueLabel);
    }

    if (row.days[shift.date]) {
      row.days[shift.date].push(shift);
    }

    row.totalHours += toNumber(shift.paidHours);
  }

  const result = Array.from(map.values())
    .map((item) => {
      const venueList = Array.from(item.venues).filter(Boolean).sort();

      return {
        ...item,

        primaryVenueLabel: venueList[0] || item.primaryVenueLabel || "",
      };
    })
    .sort((a, b) => {
      if (a.employeeName !== b.employeeName) {
        return a.employeeName.localeCompare(b.employeeName);
      }

      return (a.primaryVenueLabel || "").localeCompare(b.primaryVenueLabel || "");
    });

  for (const manager of result) {
    for (const iso of Object.keys(manager.days)) {
      manager.days[iso].sort(compareShifts);
    }
  }

  return result;
});

/* =========================================================
   Actions
========================================================= */

async function resolveAccess() {
  try {
    const user = auth.currentUser;

    if (!user?.uid) {
      canView.value = false;

      return;
    }

    const userSnap = await getDoc(doc(db, "users", user.uid));

    const userData = userSnap.exists() ? userSnap.data() || {} : {};

    const userType = String(userData.user_type || "")
      .trim()
      .toLowerCase();

    canView.value = userType === "operations";
  } catch (err) {
    console.error("[GlobalGMrotaTable] resolveAccess error:", err);

    canView.value = false;
  }
}

async function loadRows() {
  if (!canView.value) {
    return;
  }

  loading.value = true;

  error.value = "";

  try {
    const venueMapPromise = loadVenueNameMap();

    const staffPromise = loadGeneralManagers();

    const shiftsPromise = loadWeekShifts();

    const [venueMap, staffSnap, shiftsSnap] = await Promise.all([
      venueMapPromise,
      staffPromise,
      shiftsPromise,
    ]);

    venueNameMap.value = venueMap;

    const normalisedStaff = staffSnap.map((r) => ({
      ...r,

      venueLabel: resolveVenueLabel({
        venueId: r.venueId,

        explicitLabel: r.venueLabel,

        venueMap,
      }),
    }));

    gmStaffRows.value = normalisedStaff;

    gmShiftRows.value = matchShiftsToGeneralManagers(
      normalisedStaff,

      shiftsSnap.map((r) => ({
        ...r,

        venueLabel: resolveVenueLabel({
          venueId: r.venueId,

          explicitLabel: r.venueLabel,

          venueMap,
        }),
      }))
    );
  } catch (err) {
    console.error("[GlobalGMrotaTable] loadRows error:", err);

    gmStaffRows.value = [];

    gmShiftRows.value = [];

    error.value = err?.message || "Failed to load General Manager rota.";
  } finally {
    loading.value = false;
  }
}

function prevWeek() {
  const current = parseISODateLocal(selectedWeekEnding.value);

  current.setDate(current.getDate() - 7);

  selectedWeekEnding.value = toISODateLocal(current);

  loadRows();
}

function nextWeek() {
  const current = parseISODateLocal(selectedWeekEnding.value);

  current.setDate(current.getDate() + 7);

  selectedWeekEnding.value = toISODateLocal(current);

  loadRows();
}

/* =========================================================
   Data loading
========================================================= */

async function loadVenueNameMap() {
  const snap = await getDocs(collection(db, "venues"));

  const out = {};

  snap.docs.forEach((docSnap) => {
    const d = docSnap.data() || {};

    out[docSnap.id] =
      firstNonEmpty(
        d.venue_name,
        d.venueName,
        d.label,
        d.name,
        d.venue_label,
        d.venueLabel
      ) || docSnap.id;
  });

  return out;
}

async function loadGeneralManagers() {
  const q = query(collectionGroup(db, "staff_details"), orderBy("staff_name", "asc"));

  const snap = await getDocs(q);

  return snap.docs
    .map((docSnap) => normaliseStaffDoc(docSnap))
    .filter((row) => isGeneralManagerTitle(row.jobTitle))
    .sort((a, b) => {
      if (a.employeeName !== b.employeeName) {
        return a.employeeName.localeCompare(b.employeeName);
      }

      return (a.venueLabel || "").localeCompare(b.venueLabel || "");
    });
}

async function loadWeekShifts() {
  const { weekStart, weekEnd } = weekBounds.value;

  const q = query(
    collectionGroup(db, "shifts"),
    where("date", ">=", weekStart),
    where("date", "<=", weekEnd),
    orderBy("date", "asc")
  );

  const snap = await getDocs(q);

  return snap.docs.map((docSnap) => normaliseShiftDoc(docSnap)).sort(compareRows);
}

function matchShiftsToGeneralManagers(gmStaff, allShifts) {
  const gmByStaffVenue = new Map();

  const gmByNameVenue = new Map();

  for (const gm of gmStaff) {
    const staffVenueKey = buildStaffVenueKey(gm.staffId, gm.venueId);

    const nameVenueKey = buildNameVenueKey(gm.employeeName, gm.venueId);

    if (staffVenueKey) {
      gmByStaffVenue.set(staffVenueKey, gm);
    }

    if (nameVenueKey) {
      gmByNameVenue.set(nameVenueKey, gm);
    }
  }

  return allShifts
    .map((shift) => {
      let gm = null;

      const shiftStaffVenueKey = buildStaffVenueKey(shift.staffId, shift.venueId);

      const shiftNameVenueKey = buildNameVenueKey(shift.employeeName, shift.venueId);

      if (shiftStaffVenueKey && gmByStaffVenue.has(shiftStaffVenueKey)) {
        gm = gmByStaffVenue.get(shiftStaffVenueKey);
      } else if (shiftNameVenueKey && gmByNameVenue.has(shiftNameVenueKey)) {
        gm = gmByNameVenue.get(shiftNameVenueKey);
      } else if (isGeneralManagerTitle(shift.jobTitle)) {
        gm = {
          key: buildManagerKey({
            staffId: shift.staffId,

            employeeName: shift.employeeName,

            venueId: shift.venueId,
          }),

          staffId: shift.staffId,

          employeeName: shift.employeeName,

          venueId: shift.venueId,

          venueLabel: shift.venueLabel,

          nameKey: normaliseNameKey(shift.employeeName),
        };
      }

      if (!gm) {
        return null;
      }

      return {
        ...shift,

        managerKey: gm.key,

        employeeName: gm.employeeName,

        key: `${gm.key}__${shift.id}__${shift.date}__${shift.startTime}__${shift.venueId}`,
      };
    })
    .filter(Boolean);
}

/* =========================================================
   Normalisation
========================================================= */

function normaliseStaffDoc(docSnap) {
  const d = docSnap.data() || {};

  const venueId = extractVenueIdFromPath(docSnap.ref.path);

  const staffId = String(
    firstNonEmpty(d.staff_id, d.staffId, d.employeeId, d.employee_id, docSnap.id) || ""
  ).trim();

  const employeeName =
    firstNonEmpty(
      d.staff_name,
      d.staffName,
      d.employeeName,
      d.employee_name,
      d.name,
      d.fullName,
      d.full_name
    ) || "Unknown";

  const jobTitle =
    firstNonEmpty(d.job_title, d.jobTitle, d.role, d.position, d.role_name) || "—";

  const venueLabel =
    firstNonEmpty(
      d.venue_name,
      d.venueName,
      d.venue_label,
      d.venueLabel,
      d.venue_id,
      d.venueId
    ) || "";

  return {
    key: buildManagerKey({
      staffId,
      employeeName,
      venueId,
    }),

    staffId,
    employeeName,

    nameKey: normaliseNameKey(employeeName),

    jobTitle,
    venueId,
    venueLabel,
  };
}

function normaliseShiftDoc(docSnap) {
  const d = docSnap.data() || {};

  const venueId = extractVenueIdFromPath(docSnap.ref.path);

  const staffId = String(
    firstNonEmpty(d.staffId, d.staff_id, d.employeeId, d.employee_id) || ""
  ).trim();

  const employeeName =
    firstNonEmpty(
      d.staffNameSnapshot,
      d.employeeName,
      d.employee_name,
      d.staffName,
      d.staff_name,
      d.name,
      d.fullName,
      d.full_name
    ) || "Unknown";

  const jobTitle =
    firstNonEmpty(
      d.jobTitleSnapshot,
      d.jobTitle,
      d.job_title,
      d.role,
      d.position,
      d.role_name
    ) || "—";

  const date = firstNonEmpty(d.date, d.shiftDate, d.shift_date, d.trading_date) || "";

  const startTime = normaliseTime(firstNonEmpty(d.startTime, d.start_time, d.start));

  const endTime = normaliseTime(
    firstNonEmpty(d.endTime, d.end_time, d.finishTime, d.finish_time, d.end)
  );

  const breakMinutes = toNumber(
    firstNonEmpty(
      d.breakMinutes,
      d.break_minutes,
      d.unpaidBreakMinutes,
      d.unpaid_break_minutes,
      d.breakMins
    )
  );

  const paidHours = resolvePaidHours(d, startTime, endTime, breakMinutes);

  const venueLabel =
    firstNonEmpty(
      d.venue_name,
      d.venueName,
      d.venue_label,
      d.venueLabel,
      d.venue_id,
      d.venueId
    ) || "";

  return {
    id: docSnap.id,

    staffId,
    employeeName,

    employeeNameKey: normaliseNameKey(employeeName),

    jobTitle,
    date,
    startTime,
    endTime,
    breakMinutes,
    paidHours,
    venueId,
    venueLabel,
  };
}

function resolveVenueLabel({ venueId, explicitLabel, venueMap }) {
  const key = String(venueId || "").trim();

  return (
    firstNonEmpty(venueMap?.[key], venueNameMap.value[key], explicitLabel) ||
    key ||
    "Unknown venue"
  );
}

/* =========================================================
   Comparators / keys
========================================================= */

function buildManagerKey({ staffId, employeeName, venueId }) {
  const venue = String(venueId || "")
    .trim()
    .toLowerCase();

  const id = String(staffId || "").trim();

  if (id) {
    return `staff:${id}__venue:${venue}`;
  }

  return `name:${normaliseNameKey(employeeName)}__venue:${venue}`;
}

function buildStaffVenueKey(staffId, venueId) {
  const id = String(staffId || "").trim();

  const venue = String(venueId || "")
    .trim()
    .toLowerCase();

  if (!id || !venue) {
    return "";
  }

  return `staff:${id}__venue:${venue}`;
}

function buildNameVenueKey(employeeName, venueId) {
  const nameKey = normaliseNameKey(employeeName);

  const venue = String(venueId || "")
    .trim()
    .toLowerCase();

  if (!nameKey || !venue) {
    return "";
  }

  return `name:${nameKey}__venue:${venue}`;
}

function normaliseNameKey(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function compareRows(a, b) {
  if (a.employeeName !== b.employeeName) {
    return a.employeeName.localeCompare(b.employeeName);
  }

  if ((a.venueLabel || "") !== (b.venueLabel || "")) {
    return (a.venueLabel || "").localeCompare(b.venueLabel || "");
  }

  if (a.date !== b.date) {
    return a.date.localeCompare(b.date);
  }

  return (a.startTime || "").localeCompare(b.startTime || "");
}

function compareShifts(a, b) {
  if ((a.startTime || "") !== (b.startTime || "")) {
    return (a.startTime || "").localeCompare(b.startTime || "");
  }

  if ((a.endTime || "") !== (b.endTime || "")) {
    return (a.endTime || "").localeCompare(b.endTime || "");
  }

  return (a.venueLabel || "").localeCompare(b.venueLabel || "");
}

function isGeneralManagerTitle(title) {
  const t = String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

  return t === "general manager" || t === "gm" || t.includes("general manager");
}

function extractVenueIdFromPath(path) {
  const parts = String(path || "").split("/");

  const idx = parts.indexOf("venues");

  return idx >= 0 ? parts[idx + 1] || "" : "";
}

/* =========================================================
   Formatting
========================================================= */

function formatDateDisplay(iso) {
  const value = callFormatter(
    ["formatDate", "formatDateShort", "formatShortDate", "formatDateDisplay"],
    iso
  );

  if (value != null) {
    return value;
  }

  if (!isISODate(iso)) {
    return "—";
  }

  return parseISODateLocal(iso).toLocaleDateString("en-GB");
}

function formatDayHeader(iso) {
  const value = callFormatter(["formatDayName", "formatWeekday", "formatDayShort"], iso);

  if (value != null) {
    return value;
  }

  if (!isISODate(iso)) {
    return "—";
  }

  return parseISODateLocal(iso).toLocaleDateString("en-GB", {
    weekday: "short",
  });
}

function formatTimeDisplay(hhmm) {
  const value = callFormatter(["formatTime", "formatTimeShort", "formatHMTime"], hhmm);

  if (value != null) {
    return value;
  }

  if (!hhmm) {
    return "—";
  }

  return String(hhmm).slice(0, 5);
}

function formatHoursDisplay(hours) {
  const value = callFormatter(
    ["formatHours", "formatHoursDecimal", "formatDecimalHours"],
    hours
  );

  if (value != null) {
    return value;
  }

  const n = round2(toNumber(hours));

  return `${n.toFixed(2)}h`;
}

function formatBreakDisplay(minutes) {
  const mins = Math.round(toNumber(minutes));

  return mins > 0 ? `${mins}m` : "—";
}

function formatShiftDisplay(startTime, endTime) {
  if (!startTime && !endTime) {
    return "—";
  }

  return `${formatTimeDisplay(startTime)} - ${formatTimeDisplay(endTime)}`;
}

function callFormatter(names, value) {
  for (const name of names) {
    if (typeof formatter[name] === "function") {
      try {
        const result = formatter[name](value);

        if (result !== undefined && result !== null && result !== "") {
          return result;
        }
      } catch (_) {}
    }
  }

  return null;
}

/* =========================================================
   auditColours
========================================================= */

function statusPct(row) {
  if (row.startTime && row.endTime) {
    return 100;
  }

  if (row.startTime || row.endTime) {
    return 80;
  }

  return 0;
}

function shiftChipStyle(row) {
  const pct = statusPct(row);

  const colour =
    typeof auditColours.venueAuditColour === "function"
      ? auditColours.venueAuditColour(pct)
      : pct >= 85
      ? "#1b5e20"
      : pct >= 75
      ? "#ff9100"
      : "#b71c1c";

  return {
    borderLeft: `4px solid ${colour}`,

    background: `${colour}12`,
  };
}

/* =========================================================
   Date helpers
========================================================= */

function getCurrentWeekEndingISO() {
  const today = new Date();

  const day = today.getDay();

  const daysUntilSunday = (7 - day) % 7;

  const sunday = new Date(today);

  sunday.setHours(0, 0, 0, 0);

  sunday.setDate(today.getDate() + daysUntilSunday);

  return toISODateLocal(sunday);
}

function getWeekBoundsFromWeekEnding(weekEndingISO) {
  const weekEndDate = parseISODateLocal(weekEndingISO);

  const weekStartDate = new Date(weekEndDate);

  weekStartDate.setDate(weekEndDate.getDate() - 6);

  return {
    weekStart: toISODateLocal(weekStartDate),

    weekEnd: toISODateLocal(weekEndDate),
  };
}

function parseISODateLocal(iso) {
  const [y, m, d] = String(iso || "")
    .split("-")
    .map(Number);

  return new Date(y, (m || 1) - 1, d || 1);
}

function toISODateLocal(date) {
  const y = date.getFullYear();

  const m = String(date.getMonth() + 1).padStart(2, "0");

  const d = String(date.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

function isISODate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

/* =========================================================
   Shift maths
========================================================= */

function resolvePaidHours(d, startTime, endTime, breakMinutes) {
  const direct = firstNonEmpty(
    d.paidHours,
    d.paid_hours,
    d.hours,
    d.totalHours,
    d.total_hours,
    d.shiftHours,
    d.shift_hours
  );

  if (direct !== undefined && direct !== null && direct !== "") {
    return round2(toNumber(direct));
  }

  return round2(calculatePaidHours(startTime, endTime, breakMinutes));
}

function calculatePaidHours(startTime, endTime, breakMinutes) {
  const start = hhmmToMinutes(startTime);

  const end = hhmmToMinutes(endTime);

  if (start == null || end == null) {
    return 0;
  }

  let duration = end - start;

  if (duration < 0) {
    duration += 24 * 60;
  }

  duration -= Math.max(0, toNumber(breakMinutes));

  return Math.max(0, duration / 60);
}

function hhmmToMinutes(value) {
  const s = normaliseTime(value);

  if (!s) {
    return null;
  }

  const [hh, mm] = s.split(":").map(Number);

  if (!Number.isFinite(hh) || !Number.isFinite(mm)) {
    return null;
  }

  return hh * 60 + mm;
}

function normaliseTime(value) {
  const raw = String(value || "").trim();

  if (!raw) {
    return "";
  }

  const m = raw.match(/^(\d{1,2}):(\d{2})/);

  if (!m) {
    return raw;
  }

  const hh = String(Number(m[1])).padStart(2, "0");

  const mm = String(Number(m[2])).padStart(2, "0");

  return `${hh}:${mm}`;
}

/* =========================================================
   Small helpers
========================================================= */

function firstNonEmpty(...values) {
  for (const v of values) {
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return v;
    }
  }

  return "";
}

function toNumber(value) {
  const n = Number(value);

  return Number.isFinite(n) ? n : 0;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}
</script>

<style scoped>
.gm-rota-table {
  min-width: 1180px;
}

.gm-rota-table th,
.gm-rota-table td {
  vertical-align: top;
}

.staff-col {
  min-width: 220px;
  width: 220px;
}

.day-col {
  min-width: 150px;
  width: 150px;
}

.total-col {
  min-width: 90px;
  width: 90px;
  white-space: nowrap;
}

.day-head {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.day-name {
  font-weight: 700;
}

.day-date {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
}

.shift-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.shift-chip-wrap {
  display: block;
}

.shift-chip {
  border-radius: 10px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.03);
}

.shift-chip-time {
  font-weight: 700;
  font-size: 13px;
  line-height: 1.35;
}

.shift-chip-meta {
  font-size: 12px;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.68);
  margin-top: 2px;
}

.empty-cell {
  color: rgba(0, 0, 0, 0.38);
  padding-top: 6px;
}

.global-gm-rota-root {
  width: 100%;
}

@media only screen and (max-width: 992px) {
  .staff-col {
    min-width: 180px;
    width: 180px;
  }

  .day-col {
    min-width: 135px;
    width: 135px;
  }
}

@media (max-width: 600px) {
  .global-gm-rota-root .table-wrap {
    margin-left: -2px;
    margin-right: -2px;
  }

  .global-gm-rota-root .gm-rota-table {
    min-width: 980px;
  }

  .global-gm-rota-root .staff-col {
    min-width: 170px;
    width: 170px;
  }

  .global-gm-rota-root .day-col {
    min-width: 120px;
    width: 120px;
  }

  .global-gm-rota-root .shift-chip {
    padding: 7px 8px;
  }
}
</style>
